/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import {batch, useDispatch, useSelector} from 'react-redux';
import Box from '@material-ui/core/Box';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import isEmpty from 'lodash.isempty';
import {translate} from '@ion/core/src/locale/locale';
import {error} from '@ion/core/src/logger/logger';
import {CRUDPermissions, GenericObject} from '@ion/core/src/types/entities';
import {
  changeURLPath
} from '@ion/core/src/utils/BrowserUtils';
import {
  getDeviceIdentifierFromType,
  isMDTDevice as isValidMDTDevice
} from '@ion/core/src/utils/DeviceUtils';
import {ContentLoader} from '@ion/dls/src/components/loading/ContentLoader';
import {SnackbarMessage} from '@ion/dls/src/components/snackbars/SnackbarMessage';
import useSnackbar from '@ion/dls/src/components/snackbars/useSnackbar';
import {isSelectedDeviceSCI} from '@ion/dls/src/editors/DeviceEditorUtils';
import DeviceViewDialog from '@ion/dls/src/editors/DeviceViewDialog';
import {useExtendStyles} from '@ion/dls/src/hooks/useExtendStyles';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {DLSTheme} from '@ion/dls/src/types/types';
import {useCurrentFileHistory} from '@ion/query/src/hooks/useCurrentFileHistory';
import {useDeviceManufacturingInfo} from '@ion/query/src/hooks/useDeviceManufacturingInfo';
import {useDeviceShippingInfo} from '@ion/query/src/hooks/useDeviceShippingInfo';
import {useFileHistoryCurrent} from '@ion/query/src/hooks/useFileHistoryCurrent';
import {usePendingFileHistory} from '@ion/query/src/hooks/usePendingFileHistory';
import {
  useScheduleDataSyncOperation
} from '@ion/query/src/hooks/useScheduleDataSyncOperation';
import {useSmartSearchDevice} from '@ion/query/src/hooks/useSmartSearchDevice';
import {showJobCreateEditor} from '../../components/jobs/state';
import {ids} from '../../hooks/useDeviceDeepLink';
import {useFilePermissionsAction} from '../../hooks/useFilePermissionsAction';
import {useUrl} from '../../hooks/useUrl';
import {
  deviceDetailSelector,
  devicePermissionsSelector,
  deviceSCISelector,
  devicesViewSelector
} from '../../store/selectors/devices';
import {AlertHistoryTab} from './AlertHistoryTab';
import {DeviceDetailsTab} from './DeviceDetailsTab';
import {DeviceInfoPanel} from './DeviceInfoPanel';
import {EventsTab} from './EventsTab';
import {FilesTab} from './FilesTab';
import {HealthEventsTab} from './HealthEventsTab';
import {IdReportTab} from './IdReportTab';
import {ManufacturingTab} from './ManufacturingTab';
import {SCISettingsTab} from './SCISettingsTab';
import {hasPermission} from './SCISettingsUtils';
import {ServicesTab} from './ServicesTab';
import {ShippingWarrantyTab} from './ShippingWarrantyTab';
import {
  auditDevices,
  clearSCI,
  editAdminDevice,
  loadDeviceDetail,
  moveAdminDevices,
  swapAdminDevice,
  swapAdminDeviceConfirm,
  updateSCISetting,
  viewAdminDevice,
  viewDevice
} from './state';

export enum DeviceEditorTabs {
  DETAILS = 0,
  FILES = 1,
  SERVICES = 2,
  ID_REPORT = 3,
  HEALTH_EVENTS = 4,
  TELEMETRY_EVENTS = 5,
  ALERT_HISTORY = 6,
  MANUFACTURING = 7,
  SHIPPING = 8,
  SCI_SETTINGS = 9
}

export interface ViewDeviceDisplayOptions {
  explicitReportDate?: boolean;
  refreshDevice?: boolean;
}

export interface ViewDeviceEditorProps extends ViewDeviceDisplayOptions {
  selectedJobId?: (job: number) => void;
  includeEditAction?: boolean;
  isToggleSwitchGrid?: boolean;
  isShowColumnChooserIcon?: boolean;
  isShowRefreshButton?: boolean;
  devicesPermissions?: CRUDPermissions;
  hasCreateJob?: boolean;
  isCtcAdmin?: boolean;
  Features?: GenericObject;
  isVbuFwEnabled?: boolean;
};

export const viewDeviceStyles = (theme: DLSTheme) => ({
  panelSection : {
    background : dlsColor('white')
  },
  tabStyle : {
    fontFamily    : 'Neo Sans Std',
    fontSize      : '16px',
    fontStyle     : 'normal',
    fontWeight    : 400,
    lineHeight    : '20px',
    letterSpacing : '0px',
    textAlign     : 'center',
    textTransform : 'capitalize'
  },
  contentContainer : {
    width : '100%'
  },
  box : {
    borderBottom : `3px solid ${dlsColor('aaBlue')}`
  },
  center : {
    height         : '100%',
    display        : 'flex',
    justifyContent : 'center',
    alignItems     : 'center'
  }
});

/**
 * Display View Device Editor on click on view option from grid menu
 */
export const ViewDeviceEditor = ({
  selectedJobId,
  devicesPermissions,
  hasCreateJob,
  Features,
  includeEditAction = false,
  refreshDevice = false,
  explicitReportDate = false,
  isVbuFwEnabled = false,
  isToggleSwitchGrid = false,
  isShowColumnChooserIcon = false,
  isCtcAdmin = false,
  isShowRefreshButton = true
}: ViewDeviceEditorProps) => {
  const editorStyles              = useExtendStyles(viewDeviceStyles);
  const classes                   = editorStyles();
  const [activeTab, setActiveTab] = useState(0);
  const dispatch                  = useDispatch();
  const deviceDetail              = useSelector(deviceDetailSelector);
  const [scriptDesc, setScriptDesc] = useState('');
  const {
    assetData,
    latestIdReport,
    idReportInfo,
    SCIDeviceData,
    isSCIDevice
  } = useSelector(devicesViewSelector);
  const {
    device: populatedDevice,
    refresh: onRefreshDevice
  } = useSmartSearchDevice({id: deviceDetail?.id}, !!deviceDetail?.id);
  const {
    result: SCIResult,
    loading: SCILoading
  } = useSelector(deviceSCISelector);
  const {permissions} = useSelector(devicePermissionsSelector);

  const isLMU55Device       = deviceDetail?.type?.title.toLowerCase().includes('lmu55');
  const isMDTDevice         = isValidMDTDevice(deviceDetail);
  const deviceIdentifier    = getDeviceIdentifierFromType(deviceDetail);
  const {
    fileHistory,
    isLoading: isCurrentHistoryLoading,
    refresh: onRefreshFileHistory
  }       = useCurrentFileHistory(deviceIdentifier);
  const {
    pendingFileHistory,
    pendingHistoryLoading,
    refresh: onRefreshPendingFileHistory
  }       = usePendingFileHistory(deviceIdentifier);
  const {
    manufacturingInfo,
    refresh: onRefreshManufacturingInfo
  } = useDeviceManufacturingInfo(deviceIdentifier, isMDTDevice, !!deviceIdentifier);
  const {
    shippingInfo,
    refresh: onRefreshShippingInfo
  } = useDeviceShippingInfo(deviceIdentifier, !!deviceIdentifier);
  const {
    refresh: onRefreshCurrentFiles
  }  = useFileHistoryCurrent(deviceIdentifier);
  const {getPath, hasQueryParam} = useUrl();
  const refreshRoute             = !isEmpty(ids.filter(id => hasQueryParam(id))?.[0]);

  const hasFilePermissions = useFilePermissionsAction({Features});

  const {process, isProcessing} = useScheduleDataSyncOperation() || {};
  const {snackbar}              = translate('admin-devices:scheduleDataSync', {returnObjects: true});
  
  useEffect(() => {
    if (hasSCIPermission && SCIResult) {
      batch(() => {
        dispatch(viewAdminDevice(null, false));
        dispatch(clearSCI());
      });
    }
  }, [SCIResult]);

  const onCancel = useCallback(() => {
    if (refreshRoute) {
      changeURLPath(getPath());
    }
    dispatch(viewAdminDevice(null, false));
    dispatch(loadDeviceDetail(null));
  }, [refreshRoute]);

  const onEdit = useCallback(() => {
    // Pass null since the edit dialog now pulls in the device information itself.
    dispatch(editAdminDevice(null));
  }, []);

  const onAudit = useCallback(() => {
    dispatch(auditDevices(populatedDevice));
  }, [populatedDevice]);

  const onCreateJob = useCallback(() => {
    dispatch(showJobCreateEditor([populatedDevice], null, true));
  }, [populatedDevice]);

  const onMoveDevice = useCallback(() => {
    dispatch(moveAdminDevices([populatedDevice]));
  }, [populatedDevice]);

  const onRefresh = useCallback(() => {
    dispatch(viewDevice(populatedDevice, refreshDevice) as any);
    onRefreshDevice();
    onRefreshManufacturingInfo();
    onRefreshShippingInfo();
    onRefreshFileHistory();
    onRefreshCurrentFiles();
    onRefreshPendingFileHistory();
  }, [populatedDevice,
    refreshDevice,
    onRefreshManufacturingInfo,
    onRefreshShippingInfo,
    onRefreshFileHistory,
    onRefreshPendingFileHistory,
    onRefreshCurrentFiles
  ]);

  const onSubmit = useCallback((changedSettingsFields) => {
    dispatch(updateSCISetting(changedSettingsFields) as any);
  }, []);

  const onSwap = useCallback(() => {
    if (populatedDevice?.groups?.length || populatedDevice?.asset?.href || isSelectedDeviceSCI(populatedDevice)) {
      dispatch(swapAdminDeviceConfirm(populatedDevice));
    } else {
      dispatch(swapAdminDevice(populatedDevice));
    }
  }, [populatedDevice]);

  const {
    show: showBulkSnackbar,
    message: bulkSnackbarMessage,
    openSnackbar: onComplete,
    dismissSnackbar: dismissBulkSnackbar
  } = useSnackbar();

  const onScheduleDataSync = useCallback(() => {
    process?.({
      id : populatedDevice?.id
    }, {
      onSuccess : () => {
        onComplete(snackbar.success);
      },
      onError : (err) => {
        onComplete(snackbar.error);
        error('Unable to process the provided schedule data sync operation due to: ', err);
      }
    });
  }, [populatedDevice]);

  function handleChange(event, newValue) {
    setActiveTab(newValue);
  };

  const SCIRead = useMemo((): boolean => {
    return hasPermission(permissions, 'read', 'deviceCommand');
  }, [permissions]);

  const SCIUpdate = useMemo((): boolean => {
    return hasPermission(permissions, 'update', 'deviceCommand');
  }, [permissions]);

  const hasSCIPermission = useMemo(() => (
    SCIRead || SCIUpdate
  ), [permissions]);

  const tabs = useMemo(() => translate('admin-devices:view.tabs', {returnObjects: true}), []);

  return (
    <DeviceViewDialog open
      onCloseDialog={onCancel}
      hasCreateJob={hasCreateJob}
      hasFilePermissions={hasFilePermissions}
      devicesPermissions={devicesPermissions}
      includeEditAction={includeEditAction}
      selectedDevice={populatedDevice}
      deviceDetail={deviceDetail}
      onRefresh={onRefresh}
      onEdit={onEdit}
      onSwap={onSwap}
      onScheduleDataSync={onScheduleDataSync}
      onAudit={onAudit}
      onCreateJob={onCreateJob}
      onMoveDevice={onMoveDevice}>
      <div>
        <DeviceInfoPanel device={populatedDevice}
          deviceDetail={deviceDetail}
          latestIdReport={latestIdReport}
          isCtcAdmin={isCtcAdmin}
          explicitReportDate={explicitReportDate} />
      </div>
      <div className={classes.contentContainer}>
        <div>
          <Box className={classes.box}>
            <Tabs value={activeTab} onChange={handleChange} variant='scrollable'>
              <Tab value={DeviceEditorTabs.DETAILS} className={classes.tabStyle} label={tabs.deviceDetails} />
              <Tab value={DeviceEditorTabs.FILES} className={classes.tabStyle} label={tabs.files} />
              {!isMDTDevice && (
                <Tab value={DeviceEditorTabs.SERVICES} className={classes.tabStyle} label={tabs.services} />
              )}
              <Tab value={DeviceEditorTabs.ID_REPORT} className={classes.tabStyle} label={tabs.idReport} />
              {isLMU55Device && (
                <Tab value={DeviceEditorTabs.HEALTH_EVENTS} className={classes.tabStyle} label={tabs.healthEvent} />
              )}
              <Tab value={DeviceEditorTabs.TELEMETRY_EVENTS} className={classes.tabStyle} label={tabs.events} />
              <Tab value={DeviceEditorTabs.ALERT_HISTORY} className={classes.tabStyle} label={tabs.alertHistory} />
              <Tab value={DeviceEditorTabs.MANUFACTURING} className={classes.tabStyle} label={tabs.mfgData} />
              <Tab value={DeviceEditorTabs.SHIPPING} className={classes.tabStyle} label={tabs.shippingWarranty} />
              {hasSCIPermission && SCIDeviceData?.sciDeviceSettings && (
                <Tab value={DeviceEditorTabs.SCI_SETTINGS} className={classes.tabStyle} label={tabs.sciSettings} />
              )}
            </Tabs>
          </Box>
          <>
            <DeviceDetailsTab selectedTab={activeTab}
              deviceDetail={deviceDetail}
              Features={Features}
              isVbuFwEnabled={isVbuFwEnabled}
              devicesPermissions={devicesPermissions}
              tabIndex={DeviceEditorTabs.DETAILS}
              device={populatedDevice}
              assetData={assetData}
              manufacturingInfo={manufacturingInfo}
              onSuccess={onRefresh}
              scriptDesc={scriptDesc} />
            <FilesTab selectedTab={activeTab}
              tabIndex={DeviceEditorTabs.FILES}
              selectedJobId={selectedJobId}
              device={deviceDetail}
              currentFileHistory={fileHistory}
              isCurrentHistoryLoading={isCurrentHistoryLoading}
              pendingFileHistory={pendingFileHistory}
              pendingHistoryLoading={pendingHistoryLoading}
              isToggleSwitchGrid={isToggleSwitchGrid}
              isShowColumnChooserIcon={isShowColumnChooserIcon}
              isShowRefreshButton={isShowRefreshButton}
              setScriptDesc={setScriptDesc}/>
            {!isMDTDevice && <ServicesTab selectedTab={activeTab}
              device={populatedDevice}
              tabIndex={DeviceEditorTabs.SERVICES} />}
            <IdReportTab selectedTab={activeTab}
              isMDT={isMDTDevice}
              tabIndex={DeviceEditorTabs.ID_REPORT}
              idReportInfo={idReportInfo || undefined} />
            {isLMU55Device && (
              <HealthEventsTab selectedTab={activeTab}
                tabIndex={DeviceEditorTabs.HEALTH_EVENTS}
                device={populatedDevice}/>
            )}
            <EventsTab selectedTab={activeTab} tabIndex={DeviceEditorTabs.TELEMETRY_EVENTS} device={deviceDetail} />
            <AlertHistoryTab selectedTab={activeTab}
              tabIndex={DeviceEditorTabs.ALERT_HISTORY}
              device={populatedDevice} />
            <ManufacturingTab isCMF={isMDTDevice}
              selectedTab={activeTab}
              tabIndex={DeviceEditorTabs.MANUFACTURING}
              manufacturingInfo={manufacturingInfo} />
            <ShippingWarrantyTab selectedTab={activeTab}
              tabIndex={DeviceEditorTabs.SHIPPING}
              shippingInfo={shippingInfo} />
            {SCIDeviceData?.sciDeviceSettings && (
              <SCISettingsTab selectedTab={activeTab}
                SCIDeviceData={SCIDeviceData}
                tabIndex={DeviceEditorTabs.SCI_SETTINGS}
                noUpdate={!SCIUpdate}
                onSubmit={onSubmit}
                serialNumber={populatedDevice?.serialNumber}
                SCILoading={SCILoading}/>
            )}
          </>
        </div>
        <div className={classes.center}>
          {((isSCIDevice && !SCIDeviceData) || (isProcessing)) && <ContentLoader />}
        </div>
        <SnackbarMessage show={showBulkSnackbar}
          message={bulkSnackbarMessage}
          onDismiss={dismissBulkSnackbar} />
      </div>
    </DeviceViewDialog>
  );
};

export default ViewDeviceEditor;
